/**
 * Phone numbers: they type digits, we do the punctuation (SCOPE §9.1, §2.2-5).
 *
 * A number typed at a counter arrives in every shape — `5555550137`,
 * `555-555-0137`, `(555) 555-0137`, `+1 555 555 0137` — because every member of
 * staff punctuates differently and none of them should have to think about it.
 * Two consequences, and they pull in opposite directions unless the formatting
 * is separated from the storage:
 *
 *  - **What is stored has to be findable.** Customer search compares digits to
 *    digits through a functional trigram index, so `0137` finds
 *    `(555) 555-0137`. That works regardless of punctuation — but only if the
 *    punctuation is *consistent*, or the same shop ends up with the same
 *    customer entered three ways.
 *  - **What is typed has to be fast.** Ten keystrokes, no brackets, no dashes,
 *    no decision about which format this shop uses.
 *
 * So the field takes digits and renders the punctuation as they go, which is
 * both fewer keystrokes and one stored shape. Formatting is applied to the
 * *display*, and `phoneDigits` is what anything comparing should use.
 *
 * **North American numbering only, deliberately.** This is a shop in Kansas.
 * Anything that is not ten digits (or eleven starting with a 1) is left exactly
 * as typed rather than mangled into a shape it is not — an international number
 * forced into `(XXX) XXX-XXXX` is worse than an unformatted one, because it
 * looks correct.
 */

/** Just the digits. What search compares, and what length checks count. */
export function phoneDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Format as far as the digits go, so the field punctuates while it is typed.
 *
 * Progressive on purpose: `555` is `555`, `5551` is `(555) 1`, and the brackets
 * appear when there is something to bracket. Formatting only on a complete
 * number makes the field jump at the tenth keystroke, which is exactly when a
 * cashier is looking at the customer rather than the screen.
 */
export function formatPhoneAsTyped(value: string): string {
  const digits = phoneDigits(value);

  // A leading 1 is the country code, and nobody means it as an area code.
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;

  // Not a number this shop can format. Hand back what they typed rather than
  // inventing a shape — an international number is not broken, it is different.
  if (national.length > 10) return value.trim();

  if (national.length <= 3) return national;
  if (national.length <= 6) return `(${national.slice(0, 3)}) ${national.slice(3)}`;
  return `(${national.slice(0, 3)}) ${national.slice(3, 6)}-${national.slice(6)}`;
}

/** Ten digits, or eleven led by a country code. Anything else is incomplete. */
export function isCompletePhone(value: string): boolean {
  const digits = phoneDigits(value);
  return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
}

/**
 * Why this number is not usable yet, in a sentence, or null when it is.
 *
 * Returns the count so the message can say how far off they are — "that is 7
 * digits, a phone number is 10" is actionable where "invalid phone number" is
 * an accusation (§2.2-7).
 */
export function describeIncompletePhone(value: string): string | null {
  const digits = phoneDigits(value);
  if (digits.length === 0) return null; // Empty is not wrong; phone is optional.
  if (isCompletePhone(value)) return null;
  if (digits.length > 11) return `That is ${digits.length} digits — a phone number is 10`;
  return `That is ${digits.length} digit${digits.length === 1 ? '' : 's'} — a phone number is 10`;
}
